import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { REGEX_CONSTANT } from '@sharedModule/constants/pattern-regex.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { Country } from '@sharedModule/models/country.model';
import { selectCountry } from '@sharedModule/store/country/country.selector';
import { removeEmptyStrings } from '@sharedModule/utils/form.utils';
import { Observable, Subject, takeUntil } from 'rxjs';
import { MODE } from '../../constant/upsert-partner.constant';
import { PartnerService } from '../../services/partner.service';
import { PartnerListInterface } from '../../types/partner-list';
import {
  AddPartnerInterface,
  UpdatePartnerInterface,
} from '../../types/upsert-partner';

@Component({
  selector: 'app-upsert-partner',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
  ],
  templateUrl: './upsert-partner.component.html',
  styleUrl: './upsert-partner.component.scss',
})
export class UpsertPartnerComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.partners;
  public readonly mode = MODE;
  upsertPartnerFrom!: FormGroup;
  countryList!: Observable<Country[]>;
  isEdit = false;
  partnerDetails!: PartnerListInterface;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private partnerService: PartnerService,
    private messageHandlerService: MessageHandlerService
  ) {}

  ngOnInit(): void {
    this.upsertPartnerFromInit();
    this.countryList = this.store.select(selectCountry);
    this.isEdit = this._isEdit(this.router.url);
  }

  upsertPartnerFromInit(): void {
    this.upsertPartnerFrom = new FormGroup({
      [this.attrConstant.name.key]: new FormControl('', [Validators.required]),
      [this.attrConstant.website.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.email.key]: new FormControl('', [
        Validators.required,
        Validators.pattern(REGEX_CONSTANT.email),
      ]),
      [this.attrConstant.mode.key]: new FormControl('', [Validators.required]),
      [this.attrConstant.address1.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.address2.key]: new FormControl(''),
      [this.attrConstant.city.key]: new FormControl('', [Validators.required]),
      [this.attrConstant.zip.key]: new FormControl('', [Validators.required]),
      [this.attrConstant.country.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.phone.key]: new FormControl('', [Validators.required]),
      [this.attrConstant.logo.key]: new FormControl(''),
    });
  }

  private _isEdit = (url: string): boolean => {
    const endUrl: string[] = url.split('/');
    if (endUrl[2] == 'edit') {
      this.getPartnerDetails(endUrl[3]);
      return true;
    } else {
      return false;
    }
  };

  getPartnerDetails(partnerId: string): void {
    this.partnerService
      .getPartnerDetails({ partner_id: JSON.parse(partnerId) })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.upsertPartnerFrom.patchValue(x.response);
            this.partnerDetails = x.response;
          }
        },
      });
  }

  onSubmit(): void {
    if (this.upsertPartnerFrom.valid) {
      const formValue = this.upsertPartnerFrom.getRawValue();
      const payload = this.isEdit
        ? { partner_id: this.partnerDetails.partner_id, ...formValue }
        : formValue;
      if (this.isEdit) this.updatePartner(payload);
      else this.addPartner(payload);
    }
  }

  addPartner(formValue: AddPartnerInterface): void {
    this.partnerService
      .addPartner(removeEmptyStrings(formValue))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/partner/list']);
          }
        },
      });
  }

  updatePartner(formValue: UpdatePartnerInterface): void {
    this.partnerService
      .updatePartner(removeEmptyStrings(formValue))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/partner/list']);
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
